/* Mobile teacher: grade entry.
 * Flow: pick Class → Subject → Period → student list with score / weighted grades.
 * Reuses /grades/computed + /grades/bulk-upsert + /grades/annual + bulletin PDF.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { gradesApi, gradingApi } from '../../../lib/api';
import { enqueueGradeSave } from '../../../lib/offlineQueue';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import {
  FloppyDisk, ArrowLeft, Calculator, Plus, Trash, FilePdf, Lock, Trophy,
} from '@phosphor-icons/react';

const SMILEY_LEVELS = [
  { level: 1, emoji: '😟', label: 'À renforcer', color: '#DC2626' },
  { level: 2, emoji: '😐', label: 'En cours', color: '#D97706' },
  { level: 3, emoji: '🙂', label: 'Acquis', color: '#16A34A' },
  { level: 4, emoji: '😄', label: 'Maîtrisé', color: '#0F9D58' },
];
const SMILEY_BY_LEVEL = Object.fromEntries(SMILEY_LEVELS.map(x => [x.level, x]));
const bilanFromLevels = (vals) => {
  const nums = (vals || []).map(v => parseInt(v, 10)).filter(n => n >= 1 && n <= 4);
  if (!nums.length) return null;
  return Math.max(1, Math.min(4, Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)));
};
const sumSubScores = (obj, acts) => {
  const names = (acts || []).map(a => a.name);
  const vals = names.map(n => obj?.[n]).filter(v => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v)));
  if (!vals.length) return null;
  return Math.round(vals.reduce((a, v) => a + Number(v), 0) * 100) / 100;
};

const PERIODS = {
  trimestre: [
    { value: 'trimestre-1', label: 'Trimestre 1' },
    { value: 'trimestre-2', label: 'Trimestre 2' },
    { value: 'trimestre-3', label: 'Trimestre 3' },
  ],
  semestre: [
    { value: 'semestre-1', label: 'Semestre 1' },
    { value: 'semestre-2', label: 'Semestre 2' },
  ],
  periode: [
    { value: 'periode-1', label: 'Période 1' },
    { value: 'periode-2', label: 'Période 2' },
    { value: 'periode-3', label: 'Période 3' },
    { value: 'periode-4', label: 'Période 4' },
    { value: 'periode-5', label: 'Période 5' },
    { value: 'periode-6', label: 'Période 6' },
    { value: 'examen-1', label: 'Examen 1' },
    { value: 'examen-2', label: 'Examen 2' },
    { value: 'examen-3', label: 'Examen 3' },
  ],
};

const TeacherNotes = ({ me }) => {
  const [step, setStep] = useState('pick'); // 'pick' | 'enter'
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [classCfg, setClassCfg] = useState(null);
  const [computed, setComputed] = useState(null);
  const [grid, setGrid] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wgDialog, setWgDialog] = useState({ open: false, studentId: null });

  // Load class grading config once a class is picked
  useEffect(() => {
    if (!selectedClass) return;
    (async () => {
      try {
        const r = await gradingApi.classConfig(selectedClass);
        setClassCfg(r.data || null);
      } catch { setClassCfg(null); }
    })();
  }, [selectedClass]);

  const periodOptions = useMemo(
    () => classCfg ? (PERIODS[classCfg.period_type] || PERIODS.trimestre) : PERIODS.trimestre,
    [classCfg],
  );

  const loadComputed = useCallback(async () => {
    if (!selectedClass || !selectedSubject || !selectedPeriod) return;
    setLoading(true);
    try {
      const { data } = await gradesApi.computed(selectedClass, selectedPeriod);
      setComputed(data);
      const next = {};
      data.students.forEach(st => {
        const entry = st.subjects.find(e => e.subject === selectedSubject);
        next[st.student_id] = {
          score: entry?.score ?? null,
          appreciation: entry?.appreciation || '',
          sub_scores: entry?.sub_scores || {},
          sub_appreciations: entry?.sub_appreciations || {},
          sub_notes: entry?.sub_notes || {},
          weighted_grades: Array.isArray(entry?.weighted_grades) ? entry.weighted_grades : [],
        };
      });
      setGrid(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [selectedClass, selectedSubject, selectedPeriod]);

  const goEnter = () => {
    if (!selectedClass || !selectedSubject || !selectedPeriod) {
      toast.error('Choisissez classe, matière et période');
      return;
    }
    setStep('enter');
    loadComputed();
  };

  const isLocked = computed?.is_locked === true;
  const isWeighted = computed?.grading_mode === 'quantitative_weighted';
  const isTotal = computed?.grading_mode === 'quantitative_total';
  const isQual = computed?.grading_mode === 'qualitative';
  const subjectMeta = computed?.subjects?.find(s => s.name === selectedSubject) || null;
  const subActivities = (subjectMeta?.sub_activities || []).map(sa => (typeof sa === 'string' ? { name: sa } : sa));
  const hasActivities = subActivities.length > 0;
  const subjectMax = subjectMeta?.max_score ?? null;
  const isAppreciationSubject = subjectMeta?.type === 'appreciation';
  const isExcludedSubject = !!subjectMeta?.excluded_from_total;

  const updateSubScore = (sid, actName, v) => {
    const meta = subActivities.find(a => a.name === actName);
    let val = v === '' ? '' : parseFloat(v);
    if (val !== '' && !Number.isNaN(val) && meta?.max_score != null && val > meta.max_score) {
      val = meta.max_score;
      toast.warning(`Note plafonnée au barème (${meta.max_score}) pour « ${actName} »`);
    }
    setGrid(prev => {
      const subs = { ...(prev[sid]?.sub_scores || {}), [actName]: val };
      return { ...prev, [sid]: { ...prev[sid], sub_scores: subs, score: sumSubScores(subs, subActivities) } };
    });
  };
  const updateSubAppr = (sid, actName, v) => {
    setGrid(prev => ({ ...prev, [sid]: { ...prev[sid], sub_appreciations: { ...(prev[sid]?.sub_appreciations || {}), [actName]: v } } }));
  };
  const updateSubNote = (sid, actName, v) => {
    setGrid(prev => ({ ...prev, [sid]: { ...prev[sid], sub_notes: { ...(prev[sid]?.sub_notes || {}), [actName]: v } } }));
  };

  // ---------- Grid helpers ----------
  const updateScore = (sid, v) => {
    let val = v === '' ? null : parseFloat(v);
    if (val !== null && !Number.isNaN(val)) {
      const cap = isWeighted ? 20 : (isTotal ? subjectMax : null);
      if (cap != null && val > cap) {
        val = cap;
        toast.warning(`Note plafonnée au barème (${cap})`);
      }
    }
    setGrid(prev => ({
      ...prev,
      [sid]: { ...prev[sid], score: val },
    }));
  };
  const updateAppreciation = (sid, v) => {
    setGrid(prev => ({ ...prev, [sid]: { ...prev[sid], appreciation: v } }));
  };

  const computeWeightedAvg = (list) => {
    const valid = (list || []).filter(w => w.score !== '' && w.score !== null && !Number.isNaN(Number(w.score)) && Number(w.coef) > 0);
    if (!valid.length) return null;
    const s = valid.reduce((a, w) => a + Number(w.score) * Number(w.coef), 0);
    const c = valid.reduce((a, w) => a + Number(w.coef), 0);
    return c > 0 ? Math.round((s / c) * 100) / 100 : null;
  };
  const setWG = (sid, list) => {
    setGrid(prev => ({
      ...prev,
      [sid]: { ...prev[sid], weighted_grades: list, score: computeWeightedAvg(list) },
    }));
  };
  const addWG = (sid) => {
    const cur = grid[sid]?.weighted_grades || [];
    setWG(sid, [...cur, { name: `Note ${cur.length + 1}`, score: '', coef: 1 }]);
  };
  const removeWG = (sid, i) => setWG(sid, (grid[sid]?.weighted_grades || []).filter((_, idx) => idx !== i));
  const updateWG = (sid, i, patch) => {
    if (patch.score !== undefined && patch.score !== '' && patch.score !== null) {
      const n = parseFloat(patch.score);
      if (!Number.isNaN(n) && n > 20) {
        patch = { ...patch, score: 20 };
        toast.warning('Note plafonnée à 20');
      }
    }
    const next = (grid[sid]?.weighted_grades || []).map((w, idx) => idx === i ? { ...w, ...patch } : w);
    setWG(sid, next);
  };

  const handleSave = async () => {
    if (isLocked) { toast.error('Période verrouillée'); return; }
    setSaving(true);
    try {
      const grades = Object.entries(grid).map(([student_id, v]) => {
        const out = { student_id };
        if (!isAppreciationSubject && v.score !== null && v.score !== undefined && !Number.isNaN(v.score)) out.score = v.score;
        if (isWeighted) {
          const wgs = (v.weighted_grades || [])
            .filter(w => w.score !== '' && w.score !== null && !Number.isNaN(Number(w.score)) && Number(w.coef) > 0)
            .map(w => ({ name: w.name || '', score: Number(w.score), coef: Number(w.coef) }));
          if (wgs.length) out.weighted_grades = wgs;
        }
        if (isTotal && hasActivities) {
          const subs = Object.fromEntries(
            Object.entries(v.sub_scores || {}).filter(([, x]) => x !== '' && x !== null && x !== undefined && !Number.isNaN(Number(x))).map(([k, x]) => [k, Number(x)])
          );
          if (Object.keys(subs).length) out.sub_scores = subs;
        }
        if (isQual) {
          const subs = Object.fromEntries(Object.entries(v.sub_appreciations || {}).filter(([, x]) => x));
          if (Object.keys(subs).length) out.sub_appreciations = subs;
          const notes = Object.fromEntries(Object.entries(v.sub_notes || {}).filter(([, x]) => x && String(x).trim()));
          if (Object.keys(notes).length) out.sub_notes = notes;
        }
        if (v.appreciation) out.appreciation = v.appreciation;
        return out;
      }).filter(g => g.score !== undefined || g.weighted_grades || g.sub_scores || g.sub_appreciations || g.appreciation);
      if (grades.length === 0) { toast.error('Aucune note à enregistrer'); return; }
      const payload = {
        class_name: selectedClass,
        school_year: computed.school_year,
        period_key: selectedPeriod,
        subject: selectedSubject,
        grades,
      };
      // Try live save. If the network is down, queue for later replay.
      if (!navigator.onLine) {
        enqueueGradeSave(payload);
        toast.success(`⚠ ${grades.length} note(s) enregistrée(s) hors ligne — synchro dès reconnexion`, { duration: 4000 });
        return;
      }
      try {
        await gradesApi.bulkUpsert(payload);
        toast.success(`✓ ${grades.length} note(s) enregistrée(s)`);
        loadComputed();
      } catch (e) {
        // If the failure is a network error (no response), still queue it.
        if (!e.response) {
          enqueueGradeSave(payload);
          toast.success(`⚠ ${grades.length} note(s) enregistrée(s) hors ligne — synchro dès reconnexion`, { duration: 4000 });
        } else {
          throw e;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const downloadBulletin = async (studentId) => {
    try {
      const r = await gradesApi.bulletinPdf(studentId, selectedClass, selectedPeriod, computed.school_year);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `bulletin_${studentId}_${selectedPeriod}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Impossible de télécharger le bulletin'); }
  };

  // =========================
  // Step 1: Class / subject / period picker
  // =========================
  if (step === 'pick') {
    return (
      <div className="p-4 space-y-4" data-testid="teacher-notes-pick">
        <h2 className="font-outfit text-lg font-bold">Saisie des notes</h2>

        {/* Classes chips */}
        <section>
          <label className="text-xs font-medium text-[#64748B] uppercase mb-2 block">Classe</label>
          {me.classes.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Aucune classe assignée. Contactez l&apos;administration.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {me.classes.map(c => (
                <button
                  key={c}
                  onClick={() => setSelectedClass(c)}
                  data-testid={`pick-class-${c}`}
                  className={`p-2.5 rounded-lg border text-sm font-medium ${
                    selectedClass === c
                      ? 'border-[#0052FF] bg-[#EFF6FF] text-[#0052FF]'
                      : 'border-[#E2E8F0] text-[#64748B] active:bg-[#F1F5F9]'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Subjects — from class config once class is chosen */}
        {selectedClass && (
          <SubjectsChips
            classCfg={classCfg}
            className={selectedClass}
            defaultSubject={me.subject}
            teacherSubjects={me.subjects}
            selected={selectedSubject}
            onSelect={setSelectedSubject}
          />
        )}

        {/* Period options */}
        {selectedSubject && (
          <section>
            <label className="text-xs font-medium text-[#64748B] uppercase mb-2 block">Période</label>
            <div className="grid grid-cols-2 gap-2">
              {periodOptions.map(p => (
                <button
                  key={p.value}
                  onClick={() => setSelectedPeriod(p.value)}
                  data-testid={`pick-period-${p.value}`}
                  className={`p-2.5 rounded-lg border text-sm ${
                    selectedPeriod === p.value
                      ? 'border-[#0052FF] bg-[#EFF6FF] text-[#0052FF] font-semibold'
                      : 'border-[#E2E8F0] text-[#64748B] active:bg-[#F1F5F9]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </section>
        )}

        {selectedPeriod && (
          <Button
            onClick={goEnter}
            className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC]"
            data-testid="go-enter-notes-btn"
          >
            Commencer la saisie →
          </Button>
        )}
      </div>
    );
  }

  // =========================
  // Step 2: Notes entry
  // =========================
  return (
    <div className="pb-6" data-testid="teacher-notes-enter">
      {/* Sub-header */}
      <div className="bg-white px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2 sticky top-0 z-20">
        <button onClick={() => setStep('pick')} className="p-1.5 hover:bg-[#F1F5F9] rounded" data-testid="back-to-pick">
          <ArrowLeft size={18} className="text-[#64748B]" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-outfit font-bold text-sm text-[#0F172A] truncate">
            {selectedClass} · {selectedSubject}
            {isExcludedSubject && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-800" data-testid="m-excluded-badge">Hors moyenne</span>
            )}
            {isAppreciationSubject && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-slate-100 text-slate-700" data-testid="m-appreciation-badge">Appréciation</span>
            )}
          </div>
          <div className="text-xs text-[#64748B] truncate">
            {PERIODS[classCfg?.period_type || 'trimestre'].find(p => p.value === selectedPeriod)?.label}
            {isLocked && (
              <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-semibold" data-testid="notes-locked-badge">
                <Lock size={11} weight="bold" /> Verrouillée
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || isLocked}
          className="h-9 px-3 bg-[#10B981] hover:bg-[#059669] text-white"
          data-testid="save-notes-btn"
        >
          <FloppyDisk size={16} className="mr-1" />
          {saving ? '…' : 'Enregistrer'}
        </Button>
      </div>

      {/* Students list */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
        </div>
      ) : !computed || computed.students.length === 0 ? (
        <p className="p-6 text-center text-[#94A3B8] text-sm">Aucun élève dans cette classe.</p>
      ) : (
        <ul className="divide-y divide-[#F1F5F9]">
          {computed.students.map(st => {
            const row = grid[st.student_id] || {};
            const wgs = row.weighted_grades || [];
            const hasWG = wgs.length > 0;
            return (
              <li key={st.student_id} className="p-3 bg-white" data-testid={`student-row-${st.student_id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-outfit font-semibold text-sm truncate">
                      {st.last_name} {st.first_name}
                    </div>
                    {st.general_average != null && (
                      <div className="text-[10px] text-[#64748B]">
                        <Trophy size={9} className="inline mr-0.5" />
                        Rang général : {st.general_rank ?? '—'} · Moy. {st.general_average}/20
                      </div>
                    )}
                  </div>
                  {(isQual || (isTotal && hasActivities)) ? (
                    (() => {
                      const lvl = isQual ? bilanFromLevels(subActivities.map(sa => row.sub_appreciations?.[sa.name])) : null;
                      const m = lvl ? SMILEY_BY_LEVEL[lvl] : null;
                      return (
                        <div className="h-10 min-w-[64px] px-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center" data-testid={`m-total-${st.student_id}`}>
                          {isQual
                            ? (m ? <span className="text-sm font-bold whitespace-nowrap" style={{ color: m.color }}>{m.emoji}</span> : <span className="text-[#CBD5E1]">—</span>)
                            : <span className="text-lg font-bold text-[#0052FF]">{row.score ?? '—'}<span className="text-[10px] text-[#94A3B8]">/{subjectMeta?.max_score ?? ''}</span></span>}
                        </div>
                      );
                    })()
                  ) : isAppreciationSubject ? (
                    <div className="h-10 min-w-[64px] px-2 rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center text-[10px] text-[#94A3B8]" data-testid={`m-apprec-only-${st.student_id}`}>
                      Appréciation
                    </div>
                  ) : (
                  <Input
                    type="number" min="0" max={isWeighted ? 20 : (subjectMax ?? 20)} step="0.25"
                    value={row.score ?? ''}
                    onChange={(e) => updateScore(st.student_id, e.target.value)}
                    disabled={hasWG || isLocked}
                    className={`h-10 w-20 text-center text-lg font-semibold ${
                      hasWG ? 'bg-[#F1F5F9] cursor-not-allowed' : ''
                    }`}
                    data-testid={`m-score-${st.student_id}`}
                  />
                  )}
                  {isWeighted && (
                    <button
                      type="button"
                      onClick={() => setWgDialog({ open: true, studentId: st.student_id })}
                      disabled={isLocked}
                      title="Notes détaillées"
                      className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        hasWG ? 'bg-[#0052FF] text-white' : 'bg-[#F1F5F9] text-[#64748B]'
                      }`}
                      data-testid={`m-wg-btn-${st.student_id}`}
                    >
                      <Calculator size={16} />
                      {hasWG && <span className="text-[9px] font-bold absolute mt-6">{wgs.length}</span>}
                    </button>
                  )}
                  <button
                    onClick={() => downloadBulletin(st.student_id)}
                    className="h-10 w-10 rounded-lg bg-[#B45309]/10 text-[#B45309] flex items-center justify-center active:bg-[#B45309]/20"
                    title="Télécharger le bulletin"
                    data-testid={`m-bulletin-${st.student_id}`}
                  >
                    <FilePdf size={16} />
                  </button>
                </div>
                {/* Activities entry (primaire = scores, maternelle = smileys) */}
                {isTotal && hasActivities && (
                  <div className="mt-2 grid grid-cols-1 gap-1.5" data-testid={`m-activities-${st.student_id}`}>
                    {subActivities.map(sa => (
                      <div key={sa.name} className="flex items-center gap-2">
                        <span className="flex-1 text-xs text-[#475569] truncate">{sa.name}</span>
                        <Input
                          type="number" min="0" max={sa.max_score} step="0.25"
                          value={row.sub_scores?.[sa.name] ?? ''}
                          onChange={(e) => updateSubScore(st.student_id, sa.name, e.target.value)}
                          disabled={isLocked}
                          placeholder="0"
                          className="h-8 w-16 text-center text-sm"
                          data-testid={`m-sub-${st.student_id}-${sa.name}`}
                        />
                        <span className="text-[10px] text-[#94A3B8] w-8">/{sa.max_score}</span>
                      </div>
                    ))}
                  </div>
                )}
                {isQual && hasActivities && (
                  <div className="mt-2 grid grid-cols-1 gap-1.5" data-testid={`m-activities-${st.student_id}`}>
                    {subActivities.map(sa => (
                      <div key={sa.name} className="flex items-center gap-2">
                        <span className="flex-1 text-xs text-[#475569] truncate">{sa.name}</span>
                        <select
                          value={row.sub_appreciations?.[sa.name] || ''}
                          onChange={(e) => updateSubAppr(st.student_id, sa.name, e.target.value)}
                          disabled={isLocked}
                          className="h-8 text-xs rounded-md border border-[#E2E8F0] bg-white px-1"
                          data-testid={`m-smiley-${st.student_id}-${sa.name}`}
                        >
                          <option value="">— Non évalué</option>
                          {SMILEY_LEVELS.map(l => (
                            <option key={l.level} value={String(l.level)}>{l.emoji} {l.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                <Input
                  type="text"
                  value={row.appreciation ?? ''}
                  onChange={(e) => updateAppreciation(st.student_id, e.target.value)}
                  disabled={isLocked}
                  placeholder="Appréciation (optionnel)"
                  className="mt-2 h-8 text-xs"
                  data-testid={`m-apprec-${st.student_id}`}
                />
              </li>
            );
          })}
        </ul>
      )}

      {/* Bottom summary bar */}
      {computed && computed.students.length > 0 && !loading && (
        <div className="p-3 bg-[#F8FAFC] border-t border-[#E2E8F0] text-xs text-[#64748B] text-center">
          Classe : {computed.class_stats?.count_with_grades ?? 0} / {computed.class_stats?.count_total ?? 0} noté(s)
          {computed.class_stats?.avg != null && (
            <> · Moyenne classe <b className="text-[#0052FF]">{computed.class_stats.avg}/20</b></>
          )}
        </div>
      )}

      {/* Weighted grades dialog */}
      <WGDialog
        open={wgDialog.open}
        onClose={() => setWgDialog({ open: false, studentId: null })}
        student={computed?.students?.find(s => s.student_id === wgDialog.studentId) || null}
        subject={selectedSubject}
        row={grid[wgDialog.studentId] || {}}
        onAdd={() => addWG(wgDialog.studentId)}
        onRemove={(i) => removeWG(wgDialog.studentId, i)}
        onUpdate={(i, patch) => updateWG(wgDialog.studentId, i, patch)}
        computedAvg={computeWeightedAvg(grid[wgDialog.studentId]?.weighted_grades)}
        isLocked={isLocked}
      />
    </div>
  );
};

// ==========================
// Subject picker sub-component
// ==========================
const SubjectsChips = ({ classCfg, className, defaultSubject, teacherSubjects, selected, onSelect }) => {
  const [tmpl, setTmpl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classCfg) { setTmpl(null); return; }
    setLoading(true);
    // Load template subjects from grading templates
    import('../../../lib/api').then(({ gradingApi }) => {
      gradingApi.templates()
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : (r.data?.templates || []);
          const t = list.find(x => x.key === classCfg.template_key);
          setTmpl(t || null);
        })
        .catch(() => setTmpl(null))
        .finally(() => setLoading(false));
    });
  }, [classCfg]);

  const subjects = useMemo(() => {
    if (!tmpl?.subjects) return [];
    return tmpl.subjects.map(s => s.name);
  }, [tmpl]);

  // Teachers filter: if they have a declared subject or explicit teacher_subjects
  // mapping, restrict to those. Otherwise show all template subjects.
  const allowedSubjects = useMemo(() => {
    const teacher = new Set(teacherSubjects || []);
    if (defaultSubject) teacher.add(defaultSubject);
    if (teacher.size === 0) return subjects;
    return subjects.filter(s => teacher.has(s));
  }, [subjects, defaultSubject, teacherSubjects]);

  if (loading) return <p className="text-xs text-[#94A3B8]">Chargement des matières…</p>;
  if (subjects.length === 0) {
    return (
      <p className="text-xs text-red-500 mt-2">
        Aucun modèle de notation configuré pour {className}. Demandez à l&apos;admin.
      </p>
    );
  }

  const final = allowedSubjects.length > 0 ? allowedSubjects : subjects;
  return (
    <section>
      <label className="text-xs font-medium text-[#64748B] uppercase mb-2 block">Matière</label>
      <div className="flex flex-wrap gap-2">
        {final.map(s => (
          <button
            key={s}
            onClick={() => onSelect(s)}
            data-testid={`pick-subject-${s}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${
              selected === s
                ? 'border-[#0052FF] bg-[#EFF6FF] text-[#0052FF]'
                : 'border-[#E2E8F0] text-[#64748B]'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {allowedSubjects.length === 0 && (
        <p className="text-[10px] text-[#94A3B8] mt-1">
          (Aucune restriction de matière — vous voyez toutes celles du modèle.)
        </p>
      )}
    </section>
  );
};

// ==========================
// Weighted grades editor (mobile dialog)
// ==========================
const WGDialog = ({ open, onClose, student, subject, row, onAdd, onRemove, onUpdate, computedAvg, isLocked }) => {
  if (!student) return null;
  const wgs = row?.weighted_grades || [];
  const totalCoef = wgs.reduce((a, w) => a + (Number(w.coef) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-4" data-testid="m-wg-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-base">
            Notes détaillées
          </DialogTitle>
          <p className="text-xs text-[#64748B]">
            {student.last_name} {student.first_name} · {subject}
          </p>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {wgs.length === 0 ? (
            <p className="text-center text-sm text-[#94A3B8] py-6 border-2 border-dashed rounded">
              Ajoutez une première note
            </p>
          ) : (
            wgs.map((w, i) => (
              <Card key={i} className="border border-[#E2E8F0]" data-testid={`m-wg-row-${i}`}>
                <CardContent className="p-3 space-y-2">
                  <Input
                    value={w.name || ''}
                    onChange={(e) => onUpdate(i, { name: e.target.value })}
                    placeholder="Ex: Devoir surveillé, Composition…"
                    disabled={isLocked}
                    className="h-9 text-sm"
                    data-testid={`m-wg-name-${i}`}
                  />
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <label className="text-[10px] uppercase text-[#94A3B8]">Note /20</label>
                      <Input
                        type="number" min="0" max="20" step="0.25"
                        value={w.score ?? ''}
                        onChange={(e) => onUpdate(i, { score: e.target.value })}
                        disabled={isLocked}
                        className="h-9 text-center"
                        data-testid={`m-wg-score-${i}`}
                      />
                    </div>
                    <div className="w-20">
                      <label className="text-[10px] uppercase text-[#94A3B8]">Coef</label>
                      <Input
                        type="number" min="0.5" step="0.5"
                        value={w.coef ?? 1}
                        onChange={(e) => onUpdate(i, { coef: e.target.value })}
                        disabled={isLocked}
                        className="h-9 text-center"
                        data-testid={`m-wg-coef-${i}`}
                      />
                    </div>
                    <button
                      onClick={() => onRemove(i)}
                      disabled={isLocked}
                      className="mt-4 h-9 w-9 rounded bg-red-50 text-red-500 flex items-center justify-center"
                      data-testid={`m-wg-remove-${i}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#E2E8F0]">
          <span className="text-xs text-[#64748B]">Coef total : <b>{totalCoef}</b></span>
          <span className="text-sm font-outfit font-bold text-[#0052FF]" data-testid="m-wg-avg">
            Moyenne : {computedAvg !== null ? `${computedAvg}/20` : '—'}
          </span>
        </div>

        <DialogFooter className="flex-row gap-2 justify-between">
          <Button variant="outline" onClick={onAdd} disabled={isLocked} data-testid="m-wg-add" className="flex-1">
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
          <Button onClick={onClose} className="bg-[#0052FF] hover:bg-[#0040CC] flex-1" data-testid="m-wg-close">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherNotes;
